import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';

import { ROUTE } from '../../../../routes';
import { getSiteSeoSummary } from '../../../seo/site-seo-api';
import { resolveSummary, unaddressableView, type WebsiteCardView } from '../../card-summary';
import { useBrandSettled } from '../../useBrandSettled';
import { seoCopy } from './seo-copy';
import { deriveSeoSummary, type SeoCardInput } from './seo-summary';

/** A summary read's answer, stamped with the casino instance it was made for. */
type Read = { casinoInstanceId: string; input: SeoCardInput };

const LOADING: SeoCardInput = { status: 'loading', summary: null };

/**
 * The Website SEO card: one `GET site-seo/summary` per brand (never the full state), read only. Each read takes an id
 * from a growing counter and lands only when it is the newest; a brand switch aborts the one in flight. A brand
 * without a casino instance id ends through {@link unaddressableView} once the brand has settled.
 */
export const useSeoCard = (): WebsiteCardView => {
  const { t } = useTranslation();
  const casinoInstanceId = useAppSelector((state) => state.casinoInstance.displayBrand?.id) || undefined;
  const brandSettled = useBrandSettled();
  const [read, setRead] = React.useState<Read | null>(null);
  const readSeq = React.useRef(0);

  // Syncs with the service: one summary read per casino instance, aborted when the instance changes or the card goes.
  React.useEffect(() => {
    if (casinoInstanceId === undefined) return undefined;
    readSeq.current += 1;
    const seq = readSeq.current;
    const controller = new AbortController();
    void getSiteSeoSummary(casinoInstanceId, controller.signal).then((res) => {
      if (controller.signal.aborted || seq !== readSeq.current) return;
      const input: SeoCardInput = res.ok
        ? { status: 'ready', summary: res.value }
        : { status: res.reason === 'unavailable' ? 'unavailable' : 'failed', summary: null };
      setRead({ casinoInstanceId, input });
    });
    return () => controller.abort();
  }, [casinoInstanceId]);

  if (casinoInstanceId === undefined) {
    return unaddressableView(brandSettled, { label: seoCopy(t, { id: 'open-seo' }), to: ROUTE.seo() });
  }
  // Until this brand's read lands, the card shows it as loading, never the previous brand's answer.
  const input = read?.casinoInstanceId === casinoInstanceId ? read.input : LOADING;
  return resolveSummary(
    deriveSeoSummary(input),
    (c) => seoCopy(t, c),
    () => undefined,
  );
};
